import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common/PageHeader";

export const FEED_OPTIONS = [
  { value: "open_activities", label: "Open Activities (daily snapshot)" },
  { value: "user_activity", label: "Daily User Activity (all notes created yesterday)" },
  { value: "closed_activities", label: "Closed Activities (previous day)" },
  { value: "new_business", label: "New Business Transactions" },
  { value: "cancellations", label: "Cancellations (Policy Transaction Detail)" },
];

export function UploadPanel() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [feed, setFeed] = useState("open_activities");
  const [file, setFile] = useState(null);
  const [reportingDate, setReportingDate] = useState("");
  const [notes, setNotes] = useState("");

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("feed", feed);
      fd.append("file", file);
      if (reportingDate) fd.append("reporting_date", reportingDate);
      if (notes) fd.append("notes", notes);
      const { data } = await api.post("/imports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Received ${data.filename} (${(data.size_bytes / 1024).toFixed(0)} KB). Processing in background.`);
      setFile(null);
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["imports"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="tqi-card p-5 space-y-4" data-testid="upload-panel">
      <div>
        <div className="tqi-eyebrow">Manual upload (admin fallback)</div>
        <p className="mt-1 text-xs text-slate-600">Uses the same validation, normalization, deduplication and publishing pipeline as webhook ingestion. The original file is archived before processing.</p>
      </div>
      <Field label="Feed">
        <Select value={feed} onValueChange={setFeed}>
          <SelectTrigger data-testid="upload-feed-select"><SelectValue /></SelectTrigger>
          <SelectContent>{FEED_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`upload-feed-option-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="EZLynx CSV export">
        <Input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
      </Field>
      {feed === "open_activities" && <Field label="Snapshot date override (optional)" hint="Defaults to the receipt date in America/New_York. Use when backfilling an older snapshot."><Input type="date" value={reportingDate} onChange={(e) => setReportingDate(e.target.value)} data-testid="upload-reporting-date-input" /></Field>}
      <Field label="Notes (optional)"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Historical backfill from EZLynx export" data-testid="upload-notes-input" /></Field>
      <Button className="w-full bg-[#101C47] hover:bg-[#1B2D6E] text-white" disabled={!file || upload.isPending} onClick={() => upload.mutate()} data-testid="csv-upload-button"><UploadCloud className="mr-2 h-4 w-4" />{upload.isPending ? "Uploading…" : "Upload and process"}</Button>
    </div>
  );
}
